import type { TemplateDependencyValidationError } from '@festina/configuration-general-types/configuration'
import type { Optional } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import type { ReactNode } from 'react'

import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import { isInherited } from '../../uiModels/templateOperations/types'
import type { FieldOrigin } from '../../uiModels/types'
import { InheritanceIndicator } from '../inheritanceIndicator/InheritanceIndicator'

type Props = {
  title: string
  value: ReactNode | string
  className?: string
  diffIndicator?: ReactNode
  errors?: Optional<
    ValidationErrorWithLocation<TemplateDependencyValidationError>[]
  >
  isShowingErrors?: boolean
  path: string
  origin: Optional<FieldOrigin>
}

export const TemplateDocumentationEntry = ({
  title,
  value,
  className,
  diffIndicator,
  errors,
  isShowingErrors = false,
  path,
  origin,
}: Props) => {
  const translate = useTranslate()
  const inError = !!errors?.length
  const isFieldInherited = isInherited({ origin, path })

  return (
    <>
      <div
        className={`flex w-full flex-row items-center justify-between gap-x-4 font-medium ${
          inError ? 'text-error-500' : ''
        }`}
      >
        <div className="group-[.is-not-relevant]:opacity-20">{title}</div>
        <div className="flex flex-row">
          {!isFieldInherited && diffIndicator}
          {inError && <Alert variant="mini" severity="error" />}
          <InheritanceIndicator value={{ origin, path }} />
        </div>
      </div>
      <div className="flex flex-col gap-y-2">
        {isShowingErrors &&
          (errors ?? []).map(({ error }, index) => (
            <Alert
              // oxlint-disable-next-line react/no-array-index-key -- we don't have a unique key
              key={`${error.errorMessage}-${index}`}
              severity="error"
              title={error.errorMessage ?? translate('error.unknown')}
              className="wrap-break-word text-sm"
            />
          ))}
        <div className={className}>{value}</div>
      </div>
    </>
  )
}
